import { useState, useEffect, useCallback } from 'react';
import {
  Phone, MessageSquare, Mail, Calendar, ArrowRightLeft, UserCheck, Bot, MousePointerClick,
  FileText, Clock, ChevronDown, ChevronUp, Loader2, PhoneIncoming, PhoneOutgoing,
  MessageCircle, Send, Globe, Megaphone, ArrowDownLeft, ArrowUpRight, CalendarCheck, MapPin, ExternalLink,
  Zap, Target, CheckCircle2, XCircle, MinusCircle,
  type LucideIcon
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@easyflow/ui-frontend/components/ui/dialog';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { ScrollArea } from '@easyflow/ui-frontend/components/ui/scroll-area';
import { apiClient } from '@/lib/api';

interface TimelineItem {
  id: string;
  category: 'activity' | 'chatbot' | 'call' | 'messaging' | 'appointment';
  type: string;
  description: string;
  metadata?: Record<string, unknown>;
  user_name?: string;
  old_stage_name?: string;
  new_stage_name?: string;
  duration_seconds?: number;
  chatbot_name?: string;
  messages?: Array<{ role: string; content: string }>;
  message_count?: number;
  status?: string;
  total_tokens?: number;
  ended_at?: string;
  caller_number?: string;
  callee_number?: string;
  direction?: string;
  duration?: number;
  billsec?: number;
  hangup_cause?: string;
  recording_path?: string;
  answer_time?: string;
  match_type?: 'lead_id' | 'phone';
  contact_phone?: string;
  sender_name?: string;
  to_email?: string;
  from_email?: string;
  campaign_id?: string;
  campaign_name?: string;
  triggered_by?: string;
  outcome?: string;
  channel?: string;
  start_time?: string;
  end_time?: string;
  location?: string;
  meeting_link?: string;
  customer_name?: string;
  notes?: string;
  type_name?: string;
  type_color?: string;
  status_label?: string;
  assigned_to_name?: string;
  timestamp: string;
}

interface LeadInfo {
  source?: string;
  campaign_id?: string;
  campaign_name?: string;
  created_at?: string;
  contact_phone?: string;
  contact_email?: string;
}

interface LeadTimelineModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  leadId: string;
  leadName: string;
}

const typeConfig: Record<string, { icon: LucideIcon; color: string; label: string }> = {
  note: { icon: FileText, color: 'bg-muted text-muted-foreground', label: 'Nota' },
  call: { icon: Phone, color: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300', label: 'Chiamata' },
  sms: { icon: MessageSquare, color: 'bg-cyan-100 text-cyan-600 dark:bg-cyan-900/30 dark:text-cyan-300', label: 'SMS' },
  whatsapp: { icon: MessageCircle, color: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-300', label: 'WhatsApp' },
  telegram: { icon: Send, color: 'bg-sky-100 text-sky-600 dark:bg-sky-900/30 dark:text-sky-300', label: 'Telegram' },
  email: { icon: Mail, color: 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-300', label: 'Email' },
  meeting: { icon: Calendar, color: 'bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-300', label: 'Appuntamento' },
  stage_change: { icon: ArrowRightLeft, color: 'bg-yellow-100 text-yellow-600 dark:bg-yellow-900/30 dark:text-yellow-300', label: 'Cambio Stato' },
  assignment: { icon: UserCheck, color: 'bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-300', label: 'Assegnazione' },
  system: { icon: FileText, color: 'bg-muted text-muted-foreground', label: 'Sistema' },
  chatbot: { icon: Bot, color: 'bg-violet-100 text-violet-600 dark:bg-violet-900/30 dark:text-violet-300', label: 'Chatbot' },
  click_to_call: { icon: MousePointerClick, color: 'bg-cyan-100 text-cyan-600 dark:bg-cyan-900/30 dark:text-cyan-300', label: 'Click-to-Call' },
  form_submit: { icon: Send, color: 'bg-teal-100 text-teal-600 dark:bg-teal-900/30 dark:text-teal-300', label: 'Form Chatbot' },
  chatbot_conversation: { icon: Bot, color: 'bg-violet-100 text-violet-600 dark:bg-violet-900/30 dark:text-violet-300', label: 'Conversazione Chatbot' },
  phone_call: { icon: Phone, color: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300', label: 'Telefonata' },
  appointment: { icon: CalendarCheck, color: 'bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-300', label: 'Appuntamento' },
};

const sourceLabels: Record<string, string> = {
  form: 'Form',
  whatsapp: 'WhatsApp',
  campaign: 'Campagna',
  manual: 'Manuale',
  auto_matched: 'Auto-match',
  import: 'Importazione',
};

function formatDuration(seconds: number): string {
  if (!seconds || seconds <= 0) return '0s';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return `Oggi ${d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}`;
  } else if (diffDays === 1) {
    return `Ieri ${d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}`;
  } else {
    return d.toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  }
}

function ChatMessages({ messages }: { messages: Array<{ role: string; content: string }> }) {
  const [expanded, setExpanded] = useState(false);
  const displayMessages = expanded ? messages : messages.slice(0, 4);

  return (
    <div className="mt-2 space-y-1.5">
      {displayMessages.map((msg, i) => (
        <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
          <div className={`max-w-[85%] rounded-lg px-3 py-1.5 text-xs ${
            msg.role === 'user' ? 'bg-blue-50 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300' : 'bg-muted text-muted-foreground'
          }`}>
            {msg.content}
          </div>
        </div>
      ))}
      {messages.length > 4 && (
        <button onClick={() => setExpanded(!expanded)} className="flex items-center gap-1 text-xs text-blue-500 hover:text-blue-700">
          {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          {expanded ? 'Mostra meno' : `Mostra tutti (${messages.length} messaggi)`}
        </button>
      )}
    </div>
  );
}

function TimelineEntry({ item }: { item: TimelineItem }) {
  const config = typeConfig[item.type] || typeConfig.system;
  const Icon = config!.icon!;

  return (
    <div className="flex gap-3 pb-4 relative">
      <div className="flex flex-col items-center">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${config!.color!}`}>
          <Icon className="h-4 w-4" />
        </div>
        <div className="w-px bg-muted flex-1 mt-1" />
      </div>
      <div className="flex-1 min-w-0 pb-2">
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant="outline" className="text-xs">{config!.label!}</Badge>
          {item.direction && (item.type === 'sms' || item.type === 'whatsapp' || item.type === 'telegram' || item.type === 'email') && (
            <span className="flex items-center gap-0.5 text-xs text-muted-foreground">
              {item.direction === 'inbound' ? <ArrowDownLeft className="h-3 w-3 text-green-500" /> : <ArrowUpRight className="h-3 w-3 text-blue-500" />}
              {item.direction === 'inbound' ? 'Ricevuto' : 'Inviato'}
            </span>
          )}
          <span className="text-xs text-muted-foreground">{formatDate(item.timestamp)}</span>
          {item.user_name && <span className="text-xs text-muted-foreground">da {item.user_name}</span>}
          {item.sender_name && <span className="text-xs text-muted-foreground">da {item.sender_name}</span>}
          {item.triggered_by && (
            <Badge variant="outline" className="text-[10px] bg-violet-50 text-violet-600 dark:bg-violet-900/20 dark:text-violet-300 border-violet-200 dark:border-violet-800">
              <Zap className="h-2.5 w-2.5 mr-0.5" />
              {item.triggered_by === 'campaign_dialer' ? 'Dialer' :
               item.triggered_by === 'mass_outreach' ? 'Mass Outreach' :
               item.triggered_by === 'automation' ? 'Automazione' :
               item.triggered_by === 'automation_rule' ? 'Automazione' :
               item.triggered_by === 'workflow' ? 'Workflow' :
               item.triggered_by === 'manual' ? 'Manuale' :
               item.triggered_by === 'system' ? 'Sistema' :
               item.triggered_by === 'ai_agent' ? 'AI Agent' : item.triggered_by}
            </Badge>
          )}
          {item.outcome && (
            <Badge variant="outline" className={`text-[10px] ${
              item.outcome === 'positive' ? 'bg-green-50 text-green-600 dark:bg-green-900/20 dark:text-green-300 border-green-200 dark:border-green-800' :
              item.outcome === 'negative' ? 'bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-300 border-red-200 dark:border-red-800' :
              'bg-muted text-muted-foreground border-border'
            }`}>
              {item.outcome === 'positive' ? <CheckCircle2 className="h-2.5 w-2.5 mr-0.5" /> :
               item.outcome === 'negative' ? <XCircle className="h-2.5 w-2.5 mr-0.5" /> :
               <MinusCircle className="h-2.5 w-2.5 mr-0.5" />}
              {item.outcome === 'positive' ? 'Positivo' :
               item.outcome === 'negative' ? 'Negativo' : item.outcome}
            </Badge>
          )}
          {item.campaign_name && (
            <Badge variant="outline" className="text-[10px] bg-orange-50 text-orange-600 dark:bg-orange-900/20 dark:text-orange-300 border-orange-200 dark:border-orange-800">
              <Target className="h-2.5 w-2.5 mr-0.5" />
              {item.campaign_name}
            </Badge>
          )}
        </div>
        <p className="text-sm text-muted-foreground mt-1">{item.description}</p>

        {item.type === 'email' && item.to_email && (
          <span className="text-xs text-muted-foreground mt-0.5 block">A: {item.to_email}</span>
        )}

        {item.type === 'stage_change' && item.old_stage_name && item.new_stage_name && (
          <div className="flex items-center gap-2 mt-1 text-xs">
            <Badge variant="outline" className="bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-300">{item.old_stage_name}</Badge>
            <ArrowRightLeft className="h-3 w-3 text-muted-foreground" />
            <Badge variant="outline" className="bg-green-50 text-green-600 dark:bg-green-900/20 dark:text-green-300">{item.new_stage_name}</Badge>
          </div>
        )}

        {item.type === 'chatbot_conversation' && item.messages && item.messages.length > 0 && (
          <ChatMessages messages={item.messages} />
        )}

        {item.type === 'phone_call' && (
          <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground flex-wrap">
            {item.direction === 'inbound' ? (
              <span className="flex items-center gap-1"><PhoneIncoming className="h-3 w-3 text-green-500" /> In entrata</span>
            ) : (
              <span className="flex items-center gap-1"><PhoneOutgoing className="h-3 w-3 text-blue-500" /> In uscita</span>
            )}
            {item.billsec != null && item.billsec > 0 && (
              <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {formatDuration(item.billsec)}</span>
            )}
            {item.hangup_cause && item.hangup_cause !== 'NORMAL_CLEARING' && (
              <Badge variant="outline" className="text-xs bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-300">{item.hangup_cause}</Badge>
            )}
            {item.match_type === 'phone' && (
              <Badge variant="outline" className="text-xs bg-amber-50 text-amber-600 dark:bg-amber-900/20 dark:text-amber-300 border-amber-200 dark:border-amber-800">🔍 match numero</Badge>
            )}
          </div>
        )}

        {item.type === 'appointment' && (
          <div className="mt-1 space-y-1 text-xs text-muted-foreground">
            <div className="flex items-center gap-3 flex-wrap">
              {item.start_time && item.end_time && (
                <span className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {new Date(item.start_time).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                  {' - '}
                  {new Date(item.end_time).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                </span>
              )}
              {item.status_label && (
                <Badge variant="outline" className={`text-xs ${
                  item.status === 'completed' ? 'bg-green-50 text-green-600 dark:bg-green-900/20 dark:text-green-300' :
                  item.status === 'cancelled' ? 'bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-300' :
                  item.status === 'no_show' ? 'bg-amber-50 text-amber-600 dark:bg-amber-900/20 dark:text-amber-300' :
                  item.status === 'confirmed' ? 'bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-300' :
                  'bg-muted text-muted-foreground'
                }`}>{item.status_label}</Badge>
              )}
              {item.type_name && (
                <Badge variant="outline" className="text-xs">{item.type_name}</Badge>
              )}
            </div>
            {item.location && (
              <span className="flex items-center gap-1">
                <MapPin className="h-3 w-3" /> {item.location}
              </span>
            )}
            {item.meeting_link && (
              <a href={item.meeting_link} target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-1 text-blue-500 hover:text-blue-700">
                <ExternalLink className="h-3 w-3" /> Link meeting
              </a>
            )}
            {item.assigned_to_name && item.assigned_to_name.trim() && (
              <span>Assegnato a: {item.assigned_to_name}</span>
            )}
            {item.notes && (
              <p className="text-muted-foreground italic">{item.notes}</p>
            )}
          </div>
        )}

        {(item.duration_seconds != null && item.duration_seconds > 0) && item.type !== 'phone_call' && (
          <span className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
            <Clock className="h-3 w-3" /> {formatDuration(item.duration_seconds)}
          </span>
        )}
      </div>
    </div>
  );
}

interface ProfilingMetrics {
  voice_response_rate: number;
  preferred_channel: string | null;
  channel_counts: Record<string, number>;
  appointment_show_rate: number;
  total_interactions: number;
}

const channelLabels: Record<string, string> = {
  voice: 'Voce',
  sms: 'SMS',
  whatsapp: 'WhatsApp',
  telegram: 'Telegram',
  email: 'Email',
  appointment: 'Appuntamenti',
};

export default function LeadTimelineModal({ open, onOpenChange, leadId, leadName }: LeadTimelineModalProps) {
  const [timeline, setTimeline] = useState<TimelineItem[]>([]);
  const [leadInfo, setLeadInfo] = useState<LeadInfo | null>(null);
  const [summary, setSummary] = useState<{ activities: number; conversations: number; calls: number; messages: number; appointments: number }>({ activities: 0, conversations: 0, calls: 0, messages: 0, appointments: 0 });
  const [profilingMetrics, setProfilingMetrics] = useState<ProfilingMetrics | null>(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<string>('all');

  const loadTimeline = useCallback(async () => {
    setLoading(true);
    try {
      // Boundary: questa rotta ritorna un envelope non-standard con i campi al
      // top-level (success + timeline + lead_info + summary + profiling_metrics),
      // non dentro `data`. apiClient.get tipizza ApiResponse<unknown>: cast-once
      // documentato verso la shape reale del wire (crm-leads.routes GET /timeline).
      const res = (await apiClient.get(`/crm/leads/${leadId}/timeline`)) as unknown as {
        success: boolean;
        timeline?: TimelineItem[];
        summary?: { activities: number; conversations: number; calls: number; messages: number; appointments: number };
        lead_info?: LeadInfo | null;
        profiling_metrics?: ProfilingMetrics | null;
      };
      if (res.success) {
        setTimeline(res.timeline || []);
        setSummary(res.summary || { activities: 0, conversations: 0, calls: 0, messages: 0, appointments: 0 });
        setLeadInfo(res.lead_info || null);
        setProfilingMetrics(res.profiling_metrics || null);
      }
    } catch (err) {
      console.error('Error loading timeline:', err);
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => {
    if (open && leadId) {
      loadTimeline();
    }
  }, [open, leadId, loadTimeline]);

  const filteredTimeline = filter === 'all'
    ? timeline
    : timeline.filter(item => item.category === filter);

  const totalEvents = summary.activities + summary.conversations + summary.calls + summary.messages + (summary.appointments || 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl sm:h-[85vh] sm:flex sm:flex-col sm:overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-blue-500" />
            Timeline - {leadName || 'Lead'}
          </DialogTitle>

          {leadInfo && (
            <div className="flex flex-wrap items-center gap-2 mt-2">
              {leadInfo.source && (
                <Badge className="text-xs bg-muted text-muted-foreground">
                  <Globe className="h-3 w-3 mr-1" />
                  {sourceLabels[leadInfo.source] || leadInfo.source}
                </Badge>
              )}
              {leadInfo.campaign_name && (
                <Badge className="text-xs bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300">
                  <Megaphone className="h-3 w-3 mr-1" />
                  {leadInfo.campaign_name}
                </Badge>
              )}
              {leadInfo.created_at && (
                <Badge variant="outline" className="text-xs text-muted-foreground">
                  <Calendar className="h-3 w-3 mr-1" />
                  Ingresso: {new Date(leadInfo.created_at).toLocaleDateString('it-IT')}
                </Badge>
              )}
              {leadInfo.contact_phone && (
                <Badge variant="outline" className="text-xs text-muted-foreground">
                  <Phone className="h-3 w-3 mr-1" />
                  {leadInfo.contact_phone}
                </Badge>
              )}
              {leadInfo.contact_email && (
                <Badge variant="outline" className="text-xs text-muted-foreground">
                  <Mail className="h-3 w-3 mr-1" />
                  {leadInfo.contact_email}
                </Badge>
              )}
            </div>
          )}

          {profilingMetrics && profilingMetrics.total_interactions > 0 && (
            <div className="grid grid-cols-4 gap-2 mt-2 p-2 bg-muted rounded-lg border">
              <div className="text-center">
                <div className="text-sm font-bold text-blue-600 dark:text-blue-300">{profilingMetrics.voice_response_rate}%</div>
                <div className="text-[10px] text-muted-foreground">Risposta Voce</div>
              </div>
              <div className="text-center">
                <div className="text-sm font-bold text-green-600 dark:text-green-300">
                  {profilingMetrics.preferred_channel ? channelLabels[profilingMetrics.preferred_channel] || profilingMetrics.preferred_channel : '-'}
                </div>
                <div className="text-[10px] text-muted-foreground">Canale Preferito</div>
              </div>
              <div className="text-center">
                <div className="text-sm font-bold text-purple-600 dark:text-purple-300">{profilingMetrics.appointment_show_rate}%</div>
                <div className="text-[10px] text-muted-foreground">Show Rate</div>
              </div>
              <div className="text-center">
                <div className="text-sm font-bold text-muted-foreground">{profilingMetrics.total_interactions}</div>
                <div className="text-[10px] text-muted-foreground">Interazioni</div>
              </div>
            </div>
          )}

          <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
            <span>{summary.activities} attivit&agrave;</span>
            <span>&middot;</span>
            <span>{summary.conversations} conversazioni</span>
            <span>&middot;</span>
            <span>{summary.calls} chiamate</span>
            <span>&middot;</span>
            <span>{summary.messages} messaggi</span>
            <span>&middot;</span>
            <span>{summary.appointments || 0} appuntamenti</span>
            <span>&middot;</span>
            <span>{totalEvents} totali</span>
          </div>
        </DialogHeader>

        <div className="flex gap-1.5 mt-2 flex-wrap">
          {[
            { key: 'all', label: 'Tutto' },
            { key: 'activity', label: 'Attività' },
            { key: 'messaging', label: 'Messaggi' },
            { key: 'call', label: 'Chiamate' },
            { key: 'chatbot', label: 'Chatbot' },
            { key: 'appointment', label: 'Appuntamenti' },
          ].map(f => (
            <Button
              key={f.key}
              variant={filter === f.key ? 'default' : 'outline'}
              size="sm"
              className="text-xs h-7"
              onClick={() => setFilter(f.key)}
            >
              {f.label}
            </Button>
          ))}
        </div>

        <ScrollArea className="flex-1 min-h-0 mt-3 pr-2">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
              <span className="ml-2 text-sm text-muted-foreground">Caricamento timeline...</span>
            </div>
          ) : filteredTimeline.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Clock className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">Nessun evento trovato</p>
            </div>
          ) : (
            <div className="pl-1">
              {filteredTimeline.map(item => (
                <TimelineEntry key={`${item.category}-${item.id}`} item={item} />
              ))}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
